import { Textarea, type TextareaProps, Group, Text } from '@mantine/core';

export interface CharacterCountTextareaProps extends Omit<TextareaProps, 'maxLength' | 'onChange'> {
  value: string;
  charLimit: number;
  onChange: (value: string) => void;
  /** Optional helper text shown above the input (muted/secondary). */
  helperLine?: string;
}

/**
 * Textarea wrapper that enforces a character limit by truncating input at `charLimit`
 * and renders an `X / charLimit` indicator beneath the input.
 *
 * The indicator switches to red when at/over the limit so coaches don't lose content
 * to a silent truncation.
 */
export function CharacterCountTextarea({
  value,
  charLimit,
  onChange,
  helperLine,
  description,
  ...textareaProps
}: CharacterCountTextareaProps) {
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.currentTarget.value;
    if (next.length <= charLimit) {
      onChange(next);
    } else {
      onChange(next.slice(0, charLimit));
    }
  };

  const count = value.length;
  const atLimit = count >= charLimit;

  return (
    <Textarea
      {...textareaProps}
      value={value}
      onChange={handleChange}
      maxLength={charLimit}
      description={
        <Group justify="space-between" gap={4} wrap="nowrap" mt={2}>
          <Text component="span" size="xs" c="dimmed" fs={helperLine ? 'italic' : undefined}>
            {helperLine || description || ''}
          </Text>
          <Text component="span" size="xs" c={atLimit ? 'red' : 'dimmed'} fw={atLimit ? 600 : undefined}>
            {count} / {charLimit}
          </Text>
        </Group>
      }
    />
  );
}

export default CharacterCountTextarea;
